import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Box,
  Typography,
  makeStyles,
  useTheme,
  useMediaQuery,
  Badge,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ListAltIcon from '@material-ui/icons/ListAlt';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import useApi from '../../../utils/useApi';
import { calculateValueByMargin, safeParseParticulars } from '../../../utils/utils';
import ProductSaleFormatModal from '../Common/ProductSaleFormatModal';
import { CategoryBulkPrintModal } from '../LabelPrint';
import ImportStepUpload from '../Import/ImportStepUpload';
import ImportStepReview from '../Import/ImportStepReview';
import ImportStepSummary from '../Import/ImportStepSummary';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    minHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      borderRadius: 0,
      minHeight: '100%',
      margin: 0,
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    flex: 1,
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.25),
    },
  },
  tabsWrapper: {
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    padding: '0 16px',
  },
  tabRoot: {
    minHeight: 48,
    fontWeight: 600,
    fontSize: '0.85rem',
    textTransform: 'none',
    color: '#64748b',
    '&.Mui-selected': {
      color: '#17497F',
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 0,
      padding: '6px 12px',
    },
  },
  tabLabel: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
}));

const ProductImportModal = ({ open, handleClose, onReload }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [activeStep, setActiveStep] = useState(0); // 0: Upload, 1: Revisão, 2: Resumo
  const [file, setFile] = useState(null);
  const [issuer, setIssuer] = useState(null);
  const [analyzedProducts, setAnalyzedProducts] = useState(null);
  const [resolutions, setResolutions] = useState({});

  const [defaultMargin, setDefaultMargin] = useState(30);
  const [defaultSupplier, setDefaultSupplier] = useState(1);
  const [defaultCategory, setDefaultCategory] = useState(1);

  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [excludedIndices, setExcludedIndices] = useState(new Set());

  // Modal de formato fracionado
  const [formatModalOpen, setFormatModalOpen] = useState(false);
  const [formatModalTargetIndex, setFormatModalTargetIndex] = useState(null);
  const [formatModalCost, setFormatModalCost] = useState(0);
  const [formatModalParentName, setFormatModalParentName] = useState('');
  const [formatModalSuggestedFactor, setFormatModalSuggestedFactor] = useState(null);
  const [formatModalCategory, setFormatModalCategory] = useState(null);
  const [formatModalParentUnit, setFormatModalParentUnit] = useState('UN');

  // Modal de impressão de etiquetas em lote
  const [printLabelsModalOpen, setPrintLabelsModalOpen] = useState(false);
  const [printAfterConfirm, setPrintAfterConfirm] = useState(true);
  const [savedPrintableItems, setSavedPrintableItems] = useState([]);

  // API de categorias
  const [categoryLoad, categoryInfo] = useApi({
    method: 'get',
    url: '/products/categories',
  });

  // API de fornecedores
  const [supplierLoad, supplierInfo] = useApi({
    method: 'get',
    url: '/suppliers',
  });

  // API de análise de XML
  const [analyzeLoad, analyzeInfo] = useApi({
    method: 'post',
    url: '/products/import/xml',
  });

  // API de confirmação de importação
  const [confirmLoad, confirmInfo] = useApi({
    method: 'post',
    url: '/products/import/confirm',
    onCompleted: (response) => {
      if (response && response.data) {
        const results = response.data.results || [];
        const errors = results.filter((r) => r.status === 'ERROR');
        if (errors.length > 0) {
          const errorMsg = errors.map((e) => `• ${e.name}: ${e.message}`).join('\n');
          alert(`Atenção: Houve erro ao importar os seguintes itens:\n\n${errorMsg}`);
          return;
        }

        if (printAfterConfirm) {
          const itemsToPrint = [];
          results.forEach((r) => {
            if (r.status === 'SUCCESS' && r.product) {
              const p = r.product;
              itemsToPrint.push({
                ...p,
                _quantity: 1,
              });
              if (p.childFormats && p.childFormats.length > 0) {
                p.childFormats.forEach((fmt) => {
                  itemsToPrint.push({
                    ...fmt,
                    _quantity: 1,
                  });
                });
              }
            }
          });

          if (itemsToPrint.length > 0) {
            setSavedPrintableItems(itemsToPrint);
            setPrintLabelsModalOpen(true);
            if (onReload) onReload();
            return;
          }
        }

        onInternalClose();
        if (onReload) onReload();
      }
    },
  });

  const supplierList = supplierInfo.data || [];
  const categoryList = categoryInfo.data || [];

  useEffect(() => {
    if (open) {
      categoryLoad({ params: { isActive: 1 } });
      supplierLoad();
    }
  }, [open]);

  // Limpeza de estado ao fechar
  const onInternalClose = () => {
    setAnalyzedProducts(null);
    setFile(null);
    setIssuer(null);
    setResolutions({});
    setSelectedIndices(new Set());
    setExcludedIndices(new Set());
    setActiveStep(0);
    setSavedPrintableItems([]);
    setPrintLabelsModalOpen(false);
    handleClose();
  };

  // Upload e Parsing do XML
  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    const formData = new FormData();
    formData.append('file', selectedFile);

    analyzeLoad({
      data: formData,
      onCompleted: (res) => {
        if (res && res.data) {
          const products = res.data.products || [];
          const issuerData = res.data.issuer || null;

          setAnalyzedProducts(products);
          setIssuer(issuerData);

          // Se fornecedor foi identificado na nota, adotar como padrão
          let initialSupplier = defaultSupplier;
          if (issuerData && issuerData.matchedSupplier) {
            initialSupplier = issuerData.matchedSupplier.id;
            setDefaultSupplier(initialSupplier);
          }

          // Resoluções iniciais
          const initialResolutions = {};
          products.forEach((p, index) => {
            const isPackage = Boolean(p.isPackage);
            const itemsPerUnit = p.suggestedItemsPerUnit || 1;
            const isExisting = p.analysis?.status === 'EXISTING';
            const existingProduct = isExisting ? p.analysis.product : null;
            const existingFormats = existingProduct?.childFormats || [];

            // Se o produto já possui formatos fracionados cadastrados no banco de dados,
            // seleciona automaticamente 'PARENT_CHILD' e pré-monta os formatos
            let conversionMode = 'SIMPLE';
            let initialFormats = [];

            if (existingFormats.length > 0) {
              conversionMode = 'PARENT_CHILD';
              initialFormats = existingFormats.map((fmt) => ({
                id: fmt.id,
                name: fmt.name,
                fractionFactor: fmt.fractionFactor,
                value: fmt.value,
                barCode: fmt.barCode || '',
                cost: fmt.cost,
                isExisting: true,
              }));
            } else if (isPackage) {
              conversionMode = 'FRACTION_ONLY';
            }

            const unitCost = conversionMode === 'FRACTION_ONLY' ? p.cost / itemsPerUnit : p.cost;
            const saleValue = isExisting && existingProduct?.value
              ? existingProduct.value
              : calculateValueByMargin(unitCost, defaultMargin);

            const initialUnit = conversionMode === 'FRACTION_ONLY'
              ? 'UN'
              : (p.unit ? p.unit.toUpperCase().trim() : 'UN');

            initialResolutions[index] = {
              action: isExisting ? 'ADD' : 'NEW',
              productId: isExisting ? existingProduct.id : undefined,
              category: isExisting ? (existingProduct.category || defaultCategory) : defaultCategory,
              supplier: isExisting ? (existingProduct.supplier || initialSupplier) : initialSupplier,
              unit: isExisting && existingProduct?.unit ? existingProduct.unit : initialUnit,
              value: saleValue,
              margin: defaultMargin,
              conversionMode: conversionMode,
              itemsPerUnit: itemsPerUnit,
              childFormats: initialFormats,
              customName: p.name,
            };
          });

          setResolutions(initialResolutions);
        }
      },
    });
  };

  // Atualização granular de resolução
  const handleResolutionChange = (index, fieldOrUpdates, value) => {
    setResolutions((prev) => {
      const next = { ...prev };
      const current = next[index] || {};

      let updates = {};
      if (typeof fieldOrUpdates === 'string') {
        updates[fieldOrUpdates] = value;
      } else {
        updates = fieldOrUpdates;
      }

      next[index] = { ...current, ...updates };
      return next;
    });
  };

  // Seleção e Ações em Massa
  const handleToggleSelection = (index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!analyzedProducts) return;
    const activeCount = analyzedProducts.length - excludedIndices.size;
    if (selectedIndices.size === activeCount) {
      setSelectedIndices(new Set());
    } else {
      const allActive = analyzedProducts
        .map((_, i) => i)
        .filter((i) => !excludedIndices.has(i));
      setSelectedIndices(new Set(allActive));
    }
  };

  const handleRemoveProduct = (index) => {
    setExcludedIndices((prev) => new Set(prev).add(index));
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleBulkRemove = () => {
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      selectedIndices.forEach((idx) => next.add(idx));
      return next;
    });
    setSelectedIndices(new Set());
  };

  const handleBulkCategory = (categoryId) => {
    selectedIndices.forEach((idx) => {
      const res = resolutions[idx] || {};
      const p = analyzedProducts[idx];
      const isExisting = p?.analysis?.status === 'EXISTING';
      const mode = res.conversionMode || (p?.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
      const isAdoptedAsChild = mode === 'PARENT_CHILD' && res.childFormats?.some(
        (f) => String(f.id) === String(p?.analysis?.product?.id) || f.wasAdoptedAsChild,
      );
      const action = isAdoptedAsChild ? 'NEW' : (res.action || (isExisting ? 'ADD' : 'NEW'));
      if (action !== 'ADD') {
        handleResolutionChange(idx, 'category', categoryId);
        handleResolutionChange(idx, 'particulars', {});
      }
    });
    setSelectedIndices(new Set());
  };

  const handleBulkMargin = (margin) => {
    selectedIndices.forEach((idx) => {
      const p = analyzedProducts[idx];
      const res = resolutions[idx] || {};
      const mode = res.conversionMode || (p.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
      const itemsPerUnit = res.itemsPerUnit || p.suggestedItemsPerUnit || 1;
      const unitCost = mode === 'FRACTION_ONLY' ? p.cost / itemsPerUnit : p.cost;
      const newVal = calculateValueByMargin(unitCost, margin);
      handleResolutionChange(idx, { margin: margin, value: newVal });
    });
    setSelectedIndices(new Set());
  };

  const handleBulkUnit = (unitCode) => {
    selectedIndices.forEach((idx) => {
      handleResolutionChange(idx, 'unit', unitCode);
    });
    setSelectedIndices(new Set());
  };

  // Abertura do modal de criação de formatos derivados
  const handleOpenFormatModal = (index, parentCost, parentName, suggestedFactor) => {
    setFormatModalTargetIndex(index);
    setFormatModalCost(parentCost);
    setFormatModalParentName(parentName);
    const factor = suggestedFactor || resolutions[index]?.itemsPerUnit || analyzedProducts[index]?.suggestedItemsPerUnit || null;
    setFormatModalSuggestedFactor(factor);

    const rawCat = resolutions[index]?.category || defaultCategory;
    const catObj = (typeof rawCat === 'object' && rawCat !== null)
      ? rawCat
      : (categoryList.find((c) => String(c.id) === String(rawCat)) || null);
    setFormatModalCategory(catObj);

    const pUnit = analyzedProducts[index]?.unit || resolutions[index]?.unit || 'UN';
    setFormatModalParentUnit(pUnit);

    setFormatModalOpen(true);
  };

  const handleSaveFormat = (formatData) => {
    if (formatModalTargetIndex === null) return;
    const currentFormats = resolutions[formatModalTargetIndex]?.childFormats || [];
    handleResolutionChange(formatModalTargetIndex, 'childFormats', [...currentFormats, formatData]);
    setFormatModalOpen(false);
    setFormatModalTargetIndex(null);
  };

  // Validação de particularidades obrigatórias antes de avançar ou confirmar
  const validateParticulars = () => {
    if (!analyzedProducts) return true;
    for (let index = 0; index < analyzedProducts.length; index++) {
      if (excludedIndices.has(index)) continue;
      const p = analyzedProducts[index];
      const res = resolutions[index] || {};
      const mode = res.conversionMode || (p.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
      const isExisting = p.analysis?.status === 'EXISTING';
      const isAdoptedAsChild = mode === 'PARENT_CHILD' && res.childFormats?.some(
        (f) => String(f.id) === String(p.analysis?.product?.id) || f.wasAdoptedAsChild,
      );
      const action = isAdoptedAsChild ? 'NEW' : (res.action || (isExisting ? 'ADD' : 'NEW'));

      if (action !== 'ADD') {
        const catId = typeof res.category === 'object' ? res.category?.id : (res.category || defaultCategory);
        const catObj = categoryList.find((c) => String(c.id) === String(catId));
        const catParts = safeParseParticulars(catObj?.particulars);
        const userParts = res.particulars || {};
        const missing = Object.keys(catParts).filter(
          (k) => userParts[k] === undefined || userParts[k] === null || String(userParts[k]).trim() === '',
        );
        if (missing.length > 0) {
          alert(
            `Atenção: O produto "${res.customName || p.name}" precisa do preenchimento das características obrigatórias (${missing.join(', ')}) da categoria "${catObj?.name || ''}".`,
          );
          return false;
        }
      }

      if (mode === 'PARENT_CHILD' && res.childFormats && res.childFormats.length > 0) {
        for (const fmt of res.childFormats) {
          const fmtCatId = (typeof fmt.category === 'object' ? fmt.category?.id : fmt.category) || res.category || defaultCategory;
          const fmtCatObj = categoryList.find((c) => String(c.id) === String(fmtCatId));
          const fmtCatParts = safeParseParticulars(fmtCatObj?.particulars);
          const fmtUserParts = fmt.particulars || {};
          const missing = Object.keys(fmtCatParts).filter(
            (k) => fmtUserParts[k] === undefined || fmtUserParts[k] === null || String(fmtUserParts[k]).trim() === '',
          );
          if (missing.length > 0) {
            alert(
              `Atenção: A variação "${fmt.name}" precisa do preenchimento das características obrigatórias (${missing.join(', ')}) da categoria "${fmtCatObj?.name || ''}".`,
            );
            return false;
          }
        }
      }
    }
    return true;
  };

  // Confirmação final da importação
  const handleConfirmImport = () => {
    if (!analyzedProducts) return;
    if (!validateParticulars()) return;

    const productsToConfirm = analyzedProducts
      .map((p, index) => {
        if (excludedIndices.has(index)) return null;

        const res = resolutions[index] || {};
        const mode = res.conversionMode || (p.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
        const itemsPerUnit = res.itemsPerUnit || p.suggestedItemsPerUnit || 1;

        let finalAmount = p.amount;
        let finalCost = p.cost;

        if (mode === 'FRACTION_ONLY') {
          finalAmount = p.amount * itemsPerUnit;
          finalCost = p.cost / itemsPerUnit;
        }

        const isExisting = p.analysis?.status === 'EXISTING';
        const isAdoptedAsChild = mode === 'PARENT_CHILD' && res.childFormats?.some(
          (f) => String(f.id) === String(p.analysis?.product?.id) || f.wasAdoptedAsChild,
        );
        const action = isAdoptedAsChild ? 'NEW' : (res.action || (isExisting ? 'ADD' : 'NEW'));
        const finalCategory = action === 'ADD'
          ? (p.analysis?.product?.category || res.category || defaultCategory)
          : (res.category || defaultCategory);
        const saleValue = res.value !== undefined ? res.value : calculateValueByMargin(finalCost, res.margin || defaultMargin);

        let parentBarcode = p.barCode;
        if (mode === 'PARENT_CHILD' && res.childFormats?.some((f) => f.barCode === p.barCode || f.wasAdoptedAsChild)) {
          parentBarcode = undefined;
        }

        const catId = typeof finalCategory === 'object' ? finalCategory.id : finalCategory;
        const payloadItem = {
          name: res.customName || p.name,
          barCode: parentBarcode,
          amount: finalAmount,
          cost: finalCost,
          unit: res.unit || (p.unit ? p.unit.toUpperCase().trim() : 'UN'),
          action: action,
          productId: action === 'ADD' ? (res.productId || p.analysis?.product?.id) : undefined,
          supplier: res.supplier || defaultSupplier,
          category: catId,
          value: saleValue,
          particulars: action === 'ADD' ? {} : (res.particulars || {}),
        };

        if (mode === 'PARENT_CHILD' && res.childFormats && res.childFormats.length > 0) {
          payloadItem.childFormats = res.childFormats.map((fmt) => {
            const fmtCatId = (typeof fmt.category === 'object' ? fmt.category?.id : fmt.category) || catId;
            return {
              ...fmt,
              unit: fmt.unit ? fmt.unit.toUpperCase().trim() : 'UN',
              category: fmtCatId,
              particulars: fmt.particulars || {},
            };
          });
        }

        return payloadItem;
      })
      .filter(Boolean);

    confirmLoad({
      data: { products: productsToConfirm },
    });
  };

  // Itens formatados para o modal de impressão de etiquetas
  const printableItems = useMemo(() => {
    if (!analyzedProducts) return [];

    const items = [];
    analyzedProducts.forEach((p, index) => {
      if (excludedIndices.has(index)) return;

      const res = resolutions[index] || {};
      const mode = res.conversionMode || (p.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
      const itemsPerUnit = res.itemsPerUnit || p.suggestedItemsPerUnit || 1;

      let finalAmount = p.amount;
      let finalCost = p.cost;

      if (mode === 'FRACTION_ONLY') {
        finalAmount = p.amount * itemsPerUnit;
        finalCost = p.cost / itemsPerUnit;
      }

      const isExisting = p.analysis?.status === 'EXISTING';
      const isAdoptedAsChild = mode === 'PARENT_CHILD' && res.childFormats?.some(
        (f) => String(f.id) === String(p.analysis?.product?.id) || f.wasAdoptedAsChild,
      );
      const action = isAdoptedAsChild ? 'NEW' : (res.action || (isExisting ? 'ADD' : 'NEW'));
      const finalCategory = action === 'ADD'
        ? (p.analysis?.product?.category || res.category || defaultCategory)
        : (res.category || defaultCategory);
      const finalParticulars = action === 'ADD'
        ? (p.analysis?.product?.particulars || {})
        : (res.particulars || p.particulars || p.analysis?.product?.particulars || {});

      const saleValue = res.value !== undefined ? res.value : calculateValueByMargin(finalCost, res.margin || defaultMargin);

      const hasAdoptedChildWithBarcode = res.childFormats?.some(
        (f) => f.barCode === p.barCode || f.wasAdoptedAsChild,
      );
      const parentBarcode = (mode === 'PARENT_CHILD' && hasAdoptedChildWithBarcode)
        ? ''
        : (p.barCode || p.analysis?.product?.barCode || '');

      items.push({
        id: action === 'ADD' ? (res.productId || p.analysis?.product?.id) : (res.productId || `NFE-${index + 1}`),
        name: res.customName || p.name,
        value: saleValue,
        cost: finalCost,
        barCode: parentBarcode,
        amount: finalAmount,
        particulars: finalParticulars,
        category: finalCategory,
        _quantity: 1,
      });

      if (mode === 'PARENT_CHILD' && res.childFormats && res.childFormats.length > 0) {
        res.childFormats.forEach((fmt, fIdx) => {
          const factor = Number(fmt.fractionFactor || fmt.factor || 1);
          const childAmount = factor > 0 ? finalAmount * factor : 0;
          items.push({
            id: fmt.id || `CHILD-${index + 1}-${fIdx + 1}`,
            name: fmt.name,
            value: fmt.value,
            cost: fmt.cost || (finalCost / (fmt.fractionFactor || 1)),
            barCode: fmt.barCode || '',
            amount: childAmount,
            particulars: fmt.particulars || {},
            category: fmt.category || finalCategory,
            _quantity: 1,
          });
        });
      }
    });

    return items;
  }, [analyzedProducts, resolutions, excludedIndices, defaultMargin, defaultCategory]);

  return (
    <Dialog
      open={open}
      onClose={onInternalClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      classes={{ paper: classes.dialogPaper }}
    >
      <UIDialogTitle onClose={onInternalClose}>
        Importar Produtos via XML (NFe)
      </UIDialogTitle>

      {/* Tabs / Passos do Processo */}
      <Box className={classes.tabsWrapper}>
        <Tabs
          value={activeStep}
          onChange={(e, val) => {
            // Só permite avançar de aba se já houver produtos carregados
            if (val > 0 && !analyzedProducts) return;
            if (val === 2 && !validateParticulars()) return;
            setActiveStep(val);
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            className={classes.tabRoot}
            icon={<CloudUploadIcon />}
            label={<span className={classes.tabLabel}>1. Envio & Identificação</span>}
          />
          <Tab
            disabled={!analyzedProducts}
            className={classes.tabRoot}
            icon={
              <Badge
                badgeContent={analyzedProducts ? analyzedProducts.length - excludedIndices.size : 0}
                color="primary"
              >
                <ListAltIcon />
              </Badge>
            }
            label={<span className={classes.tabLabel}>2. Revisão & Variações</span>}
          />
          <Tab
            disabled={!analyzedProducts}
            className={classes.tabRoot}
            icon={<AssignmentTurnedInIcon />}
            label={<span className={classes.tabLabel}>3. Resumo & Confirmação</span>}
          />
        </Tabs>
      </Box>

      {/* Conteúdo das Etapas */}
      <DialogContent className={classes.dialogContent}>
        {activeStep === 0 && (
          <ImportStepUpload
            file={file}
            analyzing={analyzeInfo.loading}
            analyzeError={analyzeInfo.error}
            issuer={issuer}
            setIssuer={setIssuer}
            onFileSelect={handleFileChange}
            defaultSupplier={defaultSupplier}
            setDefaultSupplier={setDefaultSupplier}
            defaultCategory={defaultCategory}
            setDefaultCategory={setDefaultCategory}
            defaultMargin={defaultMargin}
            setDefaultMargin={setDefaultMargin}
            supplierList={supplierList}
            categoryList={categoryList}
            onReloadSuppliers={supplierLoad}
            onProceedToReview={() => setActiveStep(1)}
            productsCount={analyzedProducts ? analyzedProducts.length : 0}
          />
        )}

        {activeStep === 1 && analyzedProducts && (
          <ImportStepReview
            products={analyzedProducts}
            resolutions={resolutions}
            selectedIndices={selectedIndices}
            excludedIndices={excludedIndices}
            onToggleSelect={handleToggleSelection}
            onSelectAll={handleSelectAll}
            onRemoveItem={handleRemoveProduct}
            onBulkRemove={handleBulkRemove}
            onBulkCategory={handleBulkCategory}
            onBulkMargin={handleBulkMargin}
            onBulkUnit={handleBulkUnit}
            onChangeResolution={handleResolutionChange}
            categoryList={categoryList}
            onOpenFormatModal={handleOpenFormatModal}
            onBackToUpload={() => setActiveStep(0)}
            onProceedToSummary={() => {
              if (validateParticulars()) {
                setActiveStep(2);
              }
            }}
          />
        )}

        {activeStep === 2 && analyzedProducts && (
          <ImportStepSummary
            products={analyzedProducts}
            resolutions={resolutions}
            excludedIndices={excludedIndices}
            onBackToReview={() => setActiveStep(1)}
            onConfirmImport={handleConfirmImport}
            printAfterConfirm={printAfterConfirm}
            onTogglePrintAfterConfirm={setPrintAfterConfirm}
            confirming={confirmInfo.loading}
          />
        )}
      </DialogContent>

      {/* Modal de Impressão de Etiquetas em Lote */}
      <CategoryBulkPrintModal
        open={printLabelsModalOpen}
        handleClose={() => {
          setPrintLabelsModalOpen(false);
          if (savedPrintableItems.length > 0) {
            onInternalClose();
          }
        }}
        initialItems={savedPrintableItems.length > 0 ? savedPrintableItems : printableItems}
        referenceType="p"
      />

      {/* Modal Reutilizável de Formatos de Venda */}
      <ProductSaleFormatModal
        open={formatModalOpen}
        onClose={() => setFormatModalOpen(false)}
        onSave={handleSaveFormat}
        parentCost={formatModalCost}
        parentName={formatModalParentName}
        parentUnit={formatModalParentUnit}
        suggestedFactor={formatModalSuggestedFactor}
        category={formatModalCategory}
        categories={categoryList}
      />
    </Dialog>
  );
};

export default ProductImportModal;
